import * as React from "react";
import { useParams, Link } from "react-router-dom";
import {
  Space,
  Spin,
  Descriptions,
  message,
  Tag,
  Input,
  Table,
  Skeleton,
  Button,
  Popconfirm,
  Select,
  InputNumber,
  Badge,
  Tooltip,
} from "antd";

import { PlusOutlined, DeleteOutlined, EditOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { AddDatasetModal } from "components/AddDatasetModal";

import { formatFullDateFromTimeStamp, arraysHaveSameValues } from "api/utils";
import { MLModelApiCommunicator, useAmplifyOrgSubscription } from "api";

import { ListPageLayout } from "layouts";
import { TableListCellItems } from "components/Model/TableListCellItems";
const { Option } = Select;
const { TextArea } = Input;

/**
 * MLModelDetailPage Component
 * 
 * Displays comprehensive information about a specific ML model and its associated data.
 * Provides functionality for viewing, editing, and managing ML model details including
 * training configuration, dataset associations, and result management.
 * 
 * Features:
 * - Complete ML model information display
 * - Training configuration management
 * - Dataset association management
 * - Result tracking and display
 * - File download capabilities
 * - Real-time updates via Amplify subscriptions
 * - Tag management
 * - Model name and description editing
 * - Printer and material selection
 * 
 * @returns {JSX.Element} The ML model detail page component
 */
export function MLModelDetailPage() {
  const { mlmodelId } = useParams();
  return <MLModelFetchResults mlmodelId={mlmodelId} />;
}

/**
 * MLModelFetchResults Component
 * 
 * Internal component that handles the main ML model detail functionality including
 * data fetching, state management, and user interactions. This component manages
 * all the complex state and API interactions for the ML model detail view.
 * 
 * @param {Object} props - Component props
 * @param {string} props.mlmodelId - The ID of the ML model to display
 * @returns {JSX.Element} The ML model fetch results component
 */
function MLModelFetchResults({ mlmodelId }) {
  const tagPlusStyle = React.useMemo(() => {
    return { background: "#fff", borderStyle: "dashed" };
  }, []);

  const tagInputStyle = React.useMemo(() => {
    return {
      width: 64,
      height: 22,
      marginInlineEnd: 8,
      verticalAlign: "top",
    };
  }, []);

  const [messageApi, contextHolder] = message.useMessage();
  const [mlModelApiCommunicator] = React.useState(new MLModelApiCommunicator());
  const [pendingDeleteId, setPendingDeleteId] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [loadingMessage] = React.useState("Loading...");
  const [mlModel, setMLModel] = React.useState({});
  const [datasets, setDatasets] = React.useState([]);

  const [isEditingName, setIsEditingName] = React.useState(false);
  const [isEditingFriendlyName, setIsEditingFriendlyName] = React.useState(false);
  const [isEditingDescription, setIsEditingDescription] = React.useState(false);
  const [mlModelName, setMlModelName] = React.useState('');
  const [friendlyName, setFriendlyName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [addDatasetModalIsOpen, openAddDatasetModal] = React.useState(false);

  const [addTagInputVisible, setAddTagInputVisible] = React.useState(false);
  const [addTagInputValue, setAddTagInputValue] = React.useState("");
  const addTagInputRef = React.useRef(null);
  const [selectedPWAMVersion, setSelectedPWAMVersion] = React.useState('m004');
  const [maxEpochs, setMaxEpochs] = React.useState(2000);
  const [printers, setPrinters] = React.useState([]);
  const [materials, setMaterials] = React.useState([]);
  const [selectedPrinter, setSelectedPrinter] = React.useState('');
  const [selectedMaterial, setSelectedMaterial] = React.useState('');
  const [hasPrinterInteracted, setHasPrinterInteracted] = React.useState(false);
  const [hasMaterialInteracted, setHasMaterialInteracted] = React.useState(false);

  useAmplifyOrgSubscription((update) => {
    if (update.type === "mlmodel" && !loading) {
      setMLModel((currModel) =>
        currModel.mlModelId === update.data.mlModelId ? update.data : currModel
      );
    }
  });

  const fetchPageData = React.useCallback(async () => {
    try {
      console.log("fetching mlmodel", mlmodelId);
      const res = await mlModelApiCommunicator.fetch(mlmodelId);
      // sort and set
      setMLModel(res.data);
      setDatasets(res.data.datasets);
      // setProcessResultFiles(res.data.results);
      console.log("mlmodel results", res.data.results);
      const printersRes = await mlModelApiCommunicator.listPrinters();
      setPrinters(printersRes.data);
      const materialsRes = await mlModelApiCommunicator.listMaterials();
      setMaterials(materialsRes.data);
      setSelectedPrinter(res.data.printerId);
      setSelectedMaterial(res.data.materialId);
    } catch (err) {
      messageApi.error(err.message);
    }
  }, [mlmodelId, mlModelApiCommunicator, messageApi]);

  // Fetch details on mount
  React.useEffect(() => {
    setLoading(true);
    fetchPageData();
    setLoading(false);
    setHasPrinterInteracted(false);
    setHasMaterialInteracted(false);
  }, [fetchPageData]);

  const generateMLModel = React.useCallback(async () => {
    // setLoading({ active: true, message: "Sending dataset for processing..." });
    setLoading(true);
    await mlModelApiCommunicator.generateMLModel(mlmodelId, {'pwamModelVersion': selectedPWAMVersion, 'maxEpochs': maxEpochs});
    fetchPageData();
    setLoading(false);
    // setLoading({ active: false });
  }, [fetchPageData, mlModelApiCommunicator, mlmodelId, selectedPWAMVersion, maxEpochs]);

  // React.useEffect(() => {
  //   if (addTagInputVisible) {
  //     addTagInputRef.current?.focus();
  //   }
  // }, [addTagInputRef, addTagInputVisible]);

  const handleTagInputChange = React.useCallback(
    async (e) => {
      setAddTagInputValue(e.target.value);
    },
    [setAddTagInputValue]
  );

  const handleTagInputConfirm = React.useCallback(async () => {
    const modelTags = mlModel?.tags || [];
    if (addTagInputValue && !modelTags.includes(addTagInputValue)) {
      modelTags.push(addTagInputValue);
      const updateRequest = { mlModelId: mlModel.mlModelId, tags: modelTags };
      console.log("update request", updateRequest);
      try {
        await mlModelApiCommunicator.update(updateRequest);
      } catch (err) {
        console.log(err);
        messageApi.error("Error updating tags " + err.message);
      }
    }
    setAddTagInputVisible(false);
    setAddTagInputValue("");
  }, [mlModel, addTagInputValue, mlModelApiCommunicator, messageApi]);

  const removeTag = React.useCallback(
    async (removeTag) => {
      console.log("showTagInput Dialog");
      mlModel.tags = mlModel.tags.filter((tag) => tag !== removeTag);
      const updateRequest = {
        mlModelId: mlModel.mlModelId,
        tags: mlModel.tags,
      };
      try {
        await mlModelApiCommunicator.update(updateRequest);
      } catch (err) {
        console.log(err);
        messageApi.error("Error updating tags " + err.message);
      }
    },
    [mlModel, mlModelApiCommunicator, messageApi]
  );

  const showTagInput = React.useCallback(async () => {
    setAddTagInputVisible(true);
  }, [setAddTagInputVisible]);

  const confirmDeleteDataset = React.useCallback(
    async (datasetId) => {
      setPendingDeleteId("");
      setLoading(true);
      const updatePayload = {
        mlModelId: mlModel.mlModelId,
        // ...mlModel,
        /**
         * Filter out the datasetId id from the dataset update payload
         */
        datasetIds: mlModel.datasetIds.filter((id) => id !== datasetId),
      };
      try {
        await mlModelApiCommunicator.update(updatePayload);
        setTimeout(() => fetchPageData(), 1000);
      } catch (err) {
        console.log(err);
        messageApi.error("Error deleting model " + err.message);
      }
      setLoading(false);
    },
    [mlModel, fetchPageData, mlModelApiCommunicator, messageApi]
  );

  const updateModelListForDataSet = React.useCallback(
    async (updateDatasetIds) => {
      openAddDatasetModal(false);

      if (!arraysHaveSameValues(updateDatasetIds, mlModel.datasetIds)) {
        setLoading(true);
        const updateRequest = { mlModelId: mlModel.mlModelId, datasetIds: updateDatasetIds };
        try {
          await mlModelApiCommunicator.update(updateRequest);
          fetchPageData();
        } catch (err) {
          console.log(err);
          messageApi.error(err.message);
        }
        setLoading(false);
      }
    },
    [mlModel, mlModelApiCommunicator, fetchPageData, messageApi]
  );

  const onUpdateName = React.useCallback(async (mlModelName, callback) => {
    mlModel.mlModelName = mlModelName;
    const updateRequest = { mlModelId: mlModel.mlModelId, mlModelName: mlModelName };
    try {
      await mlModelApiCommunicator.update(updateRequest);
    } catch (err) {
      console.log(err);
      messageApi.error("Error updating name " + err.message);
    }

  }, [mlModel, mlModelApiCommunicator, messageApi]);

  const onUpdateFriendlyName = React.useCallback(async (friendlyName, callback) => {
    mlModel.friendlyName = friendlyName;
    const updateRequest = { mlModelId: mlModel.mlModelId, friendlyName: friendlyName };
    try {
      await mlModelApiCommunicator.update(updateRequest);
    } catch (err) {
      console.log(err);
      messageApi.error("Error updating name " + err.message);
    }

  }, [mlModel, mlModelApiCommunicator, messageApi]);

  const onUpdateDescription = React.useCallback(async (description, callback) => {
    mlModel.description = description;
    const updateRequest = { mlModelId: mlModel.mlModelId, description: description };
    try {
      await mlModelApiCommunicator.update(updateRequest);
    } catch (err) {
      console.log(err);
      messageApi.error("Error updating name " + err.message);
    }

  }, [mlModel, mlModelApiCommunicator, messageApi]);

  const onUpdatePrinterId = React.useCallback(async (printerId, callback) => {
    mlModel.printerId = printerId;
    const updateRequest = { mlModelId: mlModel.mlModelId, printerId: printerId };
    try {
      await mlModelApiCommunicator.update(updateRequest);
    } catch (err) {
      console.log(err);
      messageApi.error("Error updating printer " + err.message);
    }
  },[messageApi, mlModel, mlModelApiCommunicator]);

  const onUpdateMaterialId = React.useCallback(async (materialId, callback) => {
    mlModel.materialId = materialId;
    const updateRequest = { mlModelId: mlModel.mlModelId, materialId: materialId };
    try {
      await mlModelApiCommunicator.update(updateRequest);
    } catch (err) {
      console.log(err);
      messageApi.error("Error updating printer " + err.message);
    }
  },[messageApi, mlModel, mlModelApiCommunicator]);

  const handleEditNameClick = React.useCallback(() => {
    console.log('onEditNameClick');
    setMlModelName(mlModel.mlModelName);
    setIsEditingName(true);
  }, [mlModel]);

  const handleNameInputChange = React.useCallback((e) => {
    setMlModelName(e.target.value);
  }, [setMlModelName]);

  const handleNameBlur = React.useCallback(() => {
    setIsEditingName(false);
    if (onUpdateName) {
      onUpdateName(mlModelName);
    }
  }, [onUpdateName, mlModelName]);

  const handleNameKeyDown = React.useCallback((e) => {
    if (e.key === 'Enter') {
      handleNameBlur();
    }
  }, [handleNameBlur]);

  const handleEditDescriptionClick = React.useCallback(() => {
    console.log('onEditFriendlyNameClick');
    setDescription(mlModel.description);
    setIsEditingDescription(true);
  }, [mlModel]);

  const handleDescriptionInputChange = React.useCallback((e) => {
    setDescription(e.target.value);
  }, [setDescription]);

  const handleDescriptionBlur = React.useCallback(() => {
    setIsEditingDescription(false);
    if (onUpdateDescription) {
      onUpdateDescription(description);
    }
  }, [onUpdateDescription, description]);

  const handleDescriptionKeyDown = React.useCallback((e) => {
    if (e.key === 'Enter') {
      handleDescriptionBlur();
    }
  }, [handleDescriptionBlur]);

  const handleEditFriendlyNameClick = React.useCallback(() => {
    console.log('onEditFriendlyNameClick');
    setFriendlyName(mlModel.friendlyName);
    setIsEditingFriendlyName(true);
  }, [mlModel]);

  const handleFriendlyNameInputChange = React.useCallback((e) => {
    setFriendlyName(e.target.value);
  }, [setFriendlyName]);

  const handleFriendlyNameBlur = React.useCallback(() => {
    setIsEditingFriendlyName(false);
    if (onUpdateFriendlyName) {
      onUpdateFriendlyName(friendlyName);
    }
  }, [onUpdateFriendlyName, friendlyName]);

  const handleFriendlyNameKeyDown = React.useCallback((e) => {
    if (e.key === 'Enter') {
      handleFriendlyNameBlur();
    }
  }, [handleFriendlyNameBlur]);

  const handlePrinterChange = (value) => {
    setSelectedPrinter(value);
    setHasPrinterInteracted(true);
  };

  const handlePrinterBlur = React.useCallback(() => {
    // Handle saving the selected printer value here
    if(hasPrinterInteracted) {
      if (onUpdatePrinterId) {
        onUpdatePrinterId(selectedPrinter);
      }
    }
  }, [hasPrinterInteracted, onUpdatePrinterId, selectedPrinter]);

  const handleMaterialChange = (value) => {
    setSelectedMaterial(value);
    setHasMaterialInteracted(true);
  };

  const handleMaterialBlur = React.useCallback(() => {
    // Handle saving the selected material value here
    if(hasMaterialInteracted) {
      if (onUpdateMaterialId) {
        onUpdateMaterialId(selectedMaterial);
      }
    }
  }, [hasMaterialInteracted, onUpdateMaterialId, selectedMaterial]);

  const mlModelDetailItems = React.useMemo(() => {
    if (!mlModel) {
      return [];
    }
    return [
      {
        key: "model-name",
        label: "Model Name",
        children: 
          <span>
          {isEditingName ? (
            <input
              type="text"
              value={mlModelName}
              onChange={handleNameInputChange}
              onBlur={handleNameBlur} 
              onKeyDown={handleNameKeyDown}
              autoFocus
            />
          ) : (
            <span onClick={handleEditNameClick} style={{ cursor: 'pointer' }}>
              {mlModel && mlModel.mlModelName} <EditOutlined style={{ cursor: 'pointer' }} />
            </span>
          )}
        </span>
      },
      {
        key: "model-friendly-name",
        label: "Friendly Name",
        span: 2,
        children: 
          <span>
          {isEditingFriendlyName ? (
            <input
              type="text"
              value={friendlyName}
              onChange={handleFriendlyNameInputChange}
              onBlur={handleFriendlyNameBlur} 
              onKeyDown={handleFriendlyNameKeyDown}
              autoFocus
            />
          ) : (
            <span onClick={handleEditFriendlyNameClick} style={{ cursor: 'pointer' }}>
              {mlModel && mlModel.friendlyName} <EditOutlined style={{ cursor: 'pointer' }} />
            </span>
          )}
        </span>
      },
      {
        key: "model-description",
        label: "Description",
        span: 3,
        children: 
          <span>
          {isEditingDescription ? (
            <TextArea
              value={description}
              onChange={handleDescriptionInputChange}
              onBlur={handleDescriptionBlur}
              onKeyDown={handleDescriptionKeyDown}
              autoFocus
              rows={4} // You can adjust the number of rows as needed
            />
          ) : (
            <span onClick={handleEditDescriptionClick} style={{ cursor: 'pointer' }}>
              {mlModel && mlModel.description} <EditOutlined style={{ cursor: 'pointer' }} />
            </span>
          )}
        </span>
      },
      {
        key: "printer",
        label: "Printer",
        children: 
          <span>
              <Select
                value={selectedPrinter}
                onChange={handlePrinterChange}
                onBlur={handlePrinterBlur}
                autoFocus
                style={{ width: 200 }}
              >
                {printers.map((printer) => (
                  <Option key={printer.printerId} value={printer.printerId}>
                    {printer.printerName}
                  </Option>
                ))}
              </Select>
        </span>
      },
      {
        key: "material",
        label: "Material",
        children: 
          <span>
              <Select
                value={selectedMaterial}
                onChange={handleMaterialChange}
                onBlur={handleMaterialBlur}
                autoFocus
                style={{ width: 200 }}
              >
                {materials.map((material) => (
                  <Option key={material.materialId} value={material.materialId}>
                    {material.materialName}
                  </Option>
                ))}
              </Select>
        </span>
      },
      {
        key: "date-added",
        label: "Date Added",
        children: formatFullDateFromTimeStamp(mlModel.dateAdded),
      },
      {
        key: "mlmodel-tags",
        label: "Tags",
        children: (
          <>
            {mlModel.tags && mlModel.tags.length > 0 ? (
              mlModel.tags.map((tag) => (
                <Tag
                  key={tag}
                  color="blue"
                  closable
                  onClose={() => removeTag(tag)}
                >
                  {tag}
                </Tag>
              ))
            ) : (
              <span>No tags</span>
            )}
            {addTagInputVisible ? (
              <Input
                ref={addTagInputRef}
                type="text"
                size="small"
                style={tagInputStyle}
                value={addTagInputValue}
                onChange={handleTagInputChange}
                onBlur={handleTagInputConfirm}
                onPressEnter={handleTagInputConfirm}
              />
            ) : (
              <Tag
                style={tagPlusStyle}
                icon={<PlusOutlined />}
                onClick={showTagInput}
              >
                New Tag
              </Tag>
            )}
          </>
        ),
      },
    ];
  }, [mlModel, isEditingName, mlModelName, handleNameInputChange, handleNameBlur, handleNameKeyDown, handleEditNameClick, isEditingFriendlyName, friendlyName, handleFriendlyNameInputChange, handleFriendlyNameBlur, handleFriendlyNameKeyDown, handleEditFriendlyNameClick, isEditingDescription, description, handleDescriptionInputChange, handleDescriptionBlur, handleDescriptionKeyDown, handleEditDescriptionClick, selectedPrinter, handlePrinterBlur, printers, selectedMaterial, handleMaterialBlur, materials, addTagInputVisible, tagInputStyle, addTagInputValue, handleTagInputChange, handleTagInputConfirm, tagPlusStyle, showTagInput, removeTag]);

  const datasetColumns = React.useMemo(
    () => [
      {
        title: "Name",
        key: "datasetName",
        render: (_, r) => (
          <Link to={`/datasets/${r.datasetId}`}>
            {r.datasetName || r.datasetId}
          </Link>
        ),
      },
      {
        title: "",
        key: "action",
        render: (_, r) => (
          <Space>
            <Popconfirm
              title="Remove this model?"
              description="Are you sure you want to remove this dataset from the ml model?"
              open={r.datasetId === pendingDeleteId}
              onConfirm={() => confirmDeleteDataset(r.datasetId)}
              onCancel={() => setPendingDeleteId("")}
              okText="Yes"
              cancelText="No"
            >
              <DeleteOutlined onClick={() => setPendingDeleteId(r.datasetId)} />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [pendingDeleteId, confirmDeleteDataset]
  );

  const handleMaxEpochsChange = React.useCallback((value) => {
    console.log(`selected ${value}`);
    setMaxEpochs(value);
  }, []);


  const handlePWAMVersionSelectChange = React.useCallback((value) => {
    console.log(`selected ${value}`);
    setSelectedPWAMVersion(value);
  }, []);

    const processResultColumns = React.useMemo(
    () => [
      {
        title: "Version",
        key: "version",
        render: (_, r) => (
          <Link to={`/mlmodels/${mlmodelId}/result/${r.resultId}`}>{r.version}</Link>
        ),
      },
      {
        title: "PWAM Model Version",
        dataIndex: "pwamModelVersion",
        key: "pwamModelVersion",
        render: (_, record) => {
          return (
            <>
              <Tooltip title={`Max Epochs: ${record.options?.maxEpochs || "2000"}`}>
                <span style={{ cursor: 'pointer' }}>
                  {record.options?.pwamModelVersion || "m003"}
                  <InfoCircleOutlined style={{ marginLeft: 8 }} />
                </span>
              </Tooltip>
            </>
          );
        },
      },
      {
        title: "Status",
        key: "status",
        render: (_, r) => (
          r.status === "failed" ? (
            <Tooltip title={r.message}>
              <span>
                <TableListCellItems.StatusBadge status={r.status} />
              </span>
            </Tooltip>
          ) : (
            <TableListCellItems.StatusBadge status={r.status} />
          )
        ),
      },
      {
        title: 'Submitted',
        dataIndex: 'submittedAt',
        key: 'submittedAt',
        sorter: (a, b) => a.submittedAt - b.submittedAt,
        defaultSortOrder: 'descend',
        render: (timestamp) =>
          timestamp ? formatFullDateFromTimeStamp(timestamp) : "N/A",
      },
      {
        title: "Started",
        dataIndex: "startedAt",
        key: "resultStartedAt",
        render: (timestamp) =>
          timestamp ? formatFullDateFromTimeStamp(timestamp) : "N/A",
      },
      {
        title: "Completed",
        dataIndex: "completedAt",
        key: "resultCompletedAt",
        render: (timestamp) =>
          timestamp ? formatFullDateFromTimeStamp(timestamp) : "N/A",
      },
      {
        title: "Published",
        dataIndex: "completedAt",
        key: "resultCompletedAt",
        render: (_, r) => (
          r.published ? (
            <>
              <Badge status='success' text='Yes'/>
            </>
          ) : (
            <span>No</span>
          )
        ),
      },
    ],
    [mlmodelId]
  );

  return (
    <ListPageLayout>
      {contextHolder}
      {loading ? (
        <span>
          <Spin /> {loadingMessage}
        </span>
      ) : (
        <div>
          <Descriptions
            bordered
            items={mlModelDetailItems}
            title={
              <div>
                <h3 style={{ paddingLeft: "16px", display: "inline-block" }}>
                  ML Model - 
                </h3>
                <span> {mlModel && mlModel.mlModelName}</span>
              </div>
            }
          />
        </div>
      )}
      <Skeleton loading={loading}>
        <Table
          style={{ marginTop: 32 }}
          columns={datasetColumns}
          dataSource={datasets}
          rowKey="modelId"
          title={() => {
            return (
              <Space
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                  <h3>Included Datasets</h3>
                  <Button type="primary" onClick={() => openAddDatasetModal(true)}>Add Dataset</Button>
              </Space>
            );
          }}
        />

        <Table 
          style={{ marginTop: 32 }}
          columns={processResultColumns} 
          dataSource={mlModel?.results} 
          rowKey="resultId" 
          title={() => {
            return (
              <Space
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
              <h3>Ml Model Versions</h3>
                <Popconfirm
                  title="Process this model?"
                  description={
                    <>
                      Are you sure you want to train a new version of this model?
                      <br />
                      This action requires resource intensive processing and
                      cannot be undone.
                      <br />
                      Processing time may be lengthy. Please be patient.
                      <br/>
                      <br/>
                      USE PWAM VERSION: <Select
                        style={{ width: 140 }}
                        onChange={handlePWAMVersionSelectChange}
                        defaultValue={selectedPWAMVersion}
                      >
                        <Option value="m003">PWAM003</Option>
                        <Option value="m004">PWAM004</Option>
                        <Option value="m005">PWAM005</Option>
                      </Select>

                      <br/>
                      <br/>

                      MAX EPOCHS: 
                      <InputNumber
                        min={50}
                        max={5000}
                        defaultValue={maxEpochs}
                        onChange={handleMaxEpochsChange}
                        style={{ marginLeft: '10px' }}
                      />

                    </>
                  }
                  onConfirm={() => generateMLModel()}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button type="primary">
                    Train New Version
                  </Button>
                </Popconfirm>
              </Space>
            )
          }
          }
        />
      </Skeleton>
      <AddDatasetModal
        open={addDatasetModalIsOpen}
        startingDatasetIdSelection={mlModel.datasetIds}
        onClose={() => openAddDatasetModal(false)}
        onConfirm={updateModelListForDataSet}
      />
    </ListPageLayout>
  );
}
